// Deal u trenéra (18. 10. 2026, schválené mockupy „Uzavřít spolupráci“, „Nezačne“, „Zatím nerozhodnuto“).
// Trenér po zkušebním tréninku: uzavře spolupráci průvodcem po krocích (od 4. 11. 2026 Balíček, První trénink,
// Údaje, Shrnutí, Podpis), nebo zapíše Nezačne, nebo Nerozhodnuto. Logika na serveru: /api/deal-trener.
import React, { useEffect, useMemo, useState } from "react";
import { Handshake, UserX, UserMinus, Hourglass, ChevronLeft, ChevronRight, CircleCheck, FilePenLine, CalendarPlus, AlertTriangle, Phone, CalendarDays, MapPin, Activity, Clock, Mail, Send, Smartphone, CalendarClock, Loader2, RefreshCw } from "lucide-react";
import { Modal, HlavickaOkna } from "../ui.jsx";
import { apiFetch } from "../../lib/api.js";
import { CoRict, Volby, Cipy, Oznameni, RadekShrnuti, Fajfky, Pil, KartaObal } from "../sales/salesUi.jsx";
import { PrvniTrenink } from "../sales/NavratDialog.jsx";
import { confirmDialog } from "../confirmHost.jsx";
import PodpisQr from "../smlouva/PodpisQr.jsx";

// ─── API ─────────────────────────────────────────────────────────────────────
export async function dealGet(akce, params = {}) {
  const q = new URLSearchParams({ akce, ...Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")) });
  const r = await apiFetch(`/api/deal-trener?${q.toString()}`);
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `Chyba ${r.status}`);
  return j;
}
export async function dealPost(akce, body = {}) {
  const r = await apiFetch("/api/deal-trener", { method: "POST", body: JSON.stringify({ akce, ...body }) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) { const e = new Error(j.error || `Chyba ${r.status}`); e.kroky = j.kroky; throw e; }
  return j;
}

// ─── pomocné ─────────────────────────────────────────────────────────────────
// Koncept průvodce v zařízení (localStorage, 24 hodin). Jen pro pohodlí, server nic z něj nebere.
const KONCEPT = (id) => `fw40_deal_koncept_${id}`;
function konceptUloz(id, data) { try { localStorage.setItem(KONCEPT(id), JSON.stringify({ ...data, ulozeno: Date.now() })); } catch { /* bez úložiště */ } }
function konceptNacti(id) {
  try { const k = JSON.parse(localStorage.getItem(KONCEPT(id)) || "null"); return k && Date.now() - (k.ulozeno || 0) < 86400000 ? k : null; } catch { return null; }
}
function konceptSmaz(id) { try { localStorage.removeItem(KONCEPT(id)); } catch { /* bez úložiště */ } }
const DNY_KRATCE = ["ne", "po", "út", "st", "čt", "pá", "so"];
const dnesIso = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Prague" });
const plusDny = (iso, n) => { const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const dm = (iso) => { const [, m, d] = String(iso).split("-").map(Number); return `${d}. ${m}.`; };
const dmr = (iso) => { const [y, m, d] = String(iso).split("-").map(Number); return `${d}. ${m}. ${y}`; };
const cas = (c) => String(c || "").slice(0, 5).replace(/^0(\d)/, "$1");
const kc = (n) => `${Math.round(Number(n) || 0).toLocaleString("cs-CZ").replace(/ /g, " ")} Kč`;
function kdyZkusebni(z) {
  if (!z) return "";
  const dnes = dnesIso();
  if (z.datum === dnes) return `zkušební dnes ${cas(z.cas)}`;
  if (z.datum === plusDny(dnes, -1)) return `zkušební včera ${cas(z.cas)}`;
  return `zkušební ${dm(z.datum)}`;
}
const krestni = (j) => String(j || "").trim().split(/\s+/)[0] || "";
const sTrenerem = (j) => { const k = krestni(j); if (!k) return ""; const z = /a$/i.test(k); return `s ${z ? k.replace(/a$/i, "ou") : `${k}em`}`; };

function Poznamka({ value, onChange, placeholder, label, povinna = false, id }) {
  return (
    <label className="block space-y-1.5" htmlFor={id}>
      <span className="block text-[12px] font-bold uppercase tracking-wider text-slate-500">{label}{povinna && <span className="text-rose-600"> *</span>}</span>
      <textarea id={id} rows={3} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-[15px] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-200" />
    </label>
  );
}

// Text kroku ze serveru: **tučně** → <strong>.
function tucne(t) {
  return String(t || "").split(/\*\*(.+?)\*\*/g).map((c, i) => (i % 2 ? <strong key={i} className="font-bold text-slate-900">{c}</strong> : <React.Fragment key={i}>{c}</React.Fragment>));
}

// Výrazná poznámka o dalších trénincích (Láďa: musí být vidět i na mobilu, proto hned nahoře kroku).
function DalsiTreninky({ hotovo = false }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border-2 border-brand bg-brand-50 px-4 py-3 text-left">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand text-white"><CalendarPlus className="h-5 w-5" /></span>
      <span className="text-[16px] font-bold leading-snug text-brand-950">{hotovo ? "Další tréninky si teď naplánuj ve svém kalendáři." : "Další tréninky můžeš naplánovat hned po uzavření spolupráce ve svém kalendáři."}</span>
    </div>
  );
}

// Hlášení #185 (30. 10. 2026, varianta A): po uzavření připomenout měření na InBody.
// Změřit jde hned po zkušebním tréninku, nebo na prvním tréninku.
function MereniInBody() {
  return (
    <div className="flex items-center gap-3 rounded-2xl border-2 border-emerald-500 bg-emerald-50 px-4 py-3 text-left">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white"><Activity className="h-5 w-5" /></span>
      <span className="text-[16px] font-bold leading-snug text-emerald-950">Změř klientku na InBody, hned teď, nebo na prvním tréninku.</span>
    </div>
  );
}

// ─── Nezačne ─────────────────────────────────────────────────────────────────
const DUVODY = [
  { key: "cena", label: "Cena" }, { key: "cas", label: "Čas, nestíhá" }, { key: "trener", label: "Nesedl si s trenérem" },
  { key: "forma", label: "Nesedí forma" }, { key: "zdravi", label: "Zdraví" }, { key: "dojezd", label: "Dojezd" },
  { key: "jinde", label: "Bude cvičit jinde nebo sama" }, { key: "jiny", label: "Jiný důvod" },
];
// Láďa 17. 10.: kvůli ceně nebo dojezdu je zbytečné, aby sales volala s jiným trenérem.
const BEZ_JINEHO_TRENERA = ["cena", "dojezd"];

export function NezacneDialog({ open, polozka, onClose, onHotovo }) {
  // Láďa 18. 10. 2026: dva kroky, nejdřív Proč nezačne, pak Co dál. Volby pro sales se mění podle důvodu
  // a na jedné stránce to trenéra mátlo (vybral důvod a dole zmizela možnost).
  const [krok, setKrok] = useState(0);
  const [duvod, setDuvod] = useState("");
  const [poznamka, setPoznamka] = useState("");
  const [akce, setAkce] = useState("");
  const [ukladam, setUkladam] = useState(false);
  const [chyba, setChyba] = useState("");
  useEffect(() => { if (open) { setKrok(0); setDuvod(""); setPoznamka(""); setAkce(""); setChyba(""); } }, [open]);
  const bezJineho = BEZ_JINEHO_TRENERA.includes(duvod);
  const moznosti = [
    ...(bezJineho ? [] : [{ key: "jiny_trener", label: "Zkusit jiného trenéra", popis: "Sales jí zavolá další pracovní den" }]),
    { key: "pozdeji", label: "Ozvat se za 3 měsíce" },
    { key: "nevolat", label: "Už jí nevolat" },
  ];
  const dal = () => { setAkce(bezJineho ? "nevolat" : ""); setKrok(1); };
  const uloz = async () => {
    setUkladam(true); setChyba("");
    try { const r = await dealPost("nezacne", { zkusebni_id: polozka.id, duvod, akce_sales: akce, poznamka }); onHotovo?.(r); }
    catch (e) { setChyba(e.message); } finally { setUkladam(false); }
  };
  const nazevDuvodu = DUVODY.find((d) => d.key === duvod)?.label || "";
  return (
    <Modal open={open} onClose={onClose} size="md" plnaHlavicka
      header={<HlavickaOkna ikona={UserX} nazev="Nezačne spolupráci" popis={polozka?.jmeno} onClose={onClose} />}
      footer={krok === 0 ? <>
        <button type="button" className="btn-secondary" onClick={onClose}>Zpět</button>
        <button type="button" className="btn-primary" onClick={dal} disabled={!duvod}>Pokračovat<ChevronRight className="h-4 w-4" /></button>
      </> : <>
        <button type="button" className="btn-secondary" onClick={() => setKrok(0)} disabled={ukladam}><ChevronLeft className="h-4 w-4" />Zpět</button>
        <button type="button" className="btn-primary" onClick={uloz} disabled={!akce || ukladam}>{ukladam ? "Ukládám…" : "Uložit"}</button>
      </>}>
      <div className="space-y-4">
        <div className="text-[14px] font-bold text-brand-deep">Krok {krok + 1} ze 2: {krok === 0 ? "Proč nezačne" : "Co dál"}</div>
        {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
        {krok === 0 ? (
          <>
            <div className="space-y-2">
              <span className="block text-[12px] font-bold uppercase tracking-wider text-slate-500">Proč nezačne</span>
              <Cipy moznosti={DUVODY} value={duvod} onChange={setDuvod} />
            </div>
            <Poznamka id="nezacne-poznamka" label="Poznámka pro sales" value={poznamka} onChange={setPoznamka} placeholder="Např. ráno jí to nevyhovuje, chtěla by spíš po 18:00." />
          </>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5"><Pil ton="neutralni" ikona={UserX} casti={["důvod", nazevDuvodu]} mala /></div>
            <div className="space-y-2">
              <span className="block text-[12px] font-bold uppercase tracking-wider text-slate-500">Co dál?</span>
              <Volby name="Co dál?" moznosti={moznosti} value={akce} onChange={setAkce} />
              {bezJineho && <p className="text-[13px] text-slate-500">Kvůli ceně nebo dojezdu jiný trenér nepomůže, proto tu tahle volba není.</p>}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ─── Nerozhodnuto ────────────────────────────────────────────────────────────
export function NerozhodnutoDialog({ open, polozka, onClose, onHotovo }) {
  const zitra = plusDny(dnesIso(), 1);
  const [datum, setDatum] = useState(zitra);
  const [proc, setProc] = useState("");
  const [poznamka, setPoznamka] = useState("");
  const [ukladam, setUkladam] = useState(false);
  const [chyba, setChyba] = useState("");
  useEffect(() => { if (open) { setDatum(plusDny(dnesIso(), 1)); setProc(""); setPoznamka(""); setChyba(""); } }, [open]);
  const pozdeji = datum > zitra;
  const lzeUlozit = datum >= zitra && datum <= plusDny(dnesIso(), 30) && (!pozdeji || proc.trim().length >= 5);
  const uloz = async () => {
    setUkladam(true); setChyba("");
    try { const r = await dealPost("nerozhodnuto", { zkusebni_id: polozka.id, datum, duvod_pozdeji: pozdeji ? proc : "", poznamka }); onHotovo?.(r); }
    catch (e) { setChyba(e.message); } finally { setUkladam(false); }
  };
  return (
    <Modal open={open} onClose={onClose} size="md" plnaHlavicka
      header={<HlavickaOkna ikona={Hourglass} nazev="Zatím nerozhodnuto" popis={polozka?.jmeno} onClose={onClose} />}
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose}>Zpět</button>
        <button type="button" className="btn-primary" onClick={uloz} disabled={!lzeUlozit || ukladam}>{ukladam ? "Ukládám…" : "Uložit"}</button>
      </>}>
      <div className="space-y-4">
        {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
        <Oznameni ton="pozor"><strong>Zavolej jí nejpozději zítra.</strong> Pokud to musí být později, napiš proč. Ten den se ti hovor připomene v kartě Po zkušebním a ve Slacku.</Oznameni>
        {(Number(polozka?.nerozhodnuto_pocet) || 0) >= 1 && <Oznameni ton="problem"><strong>Tohle je poslední odklad.</strong> Při dalším hovoru musí klientka říct výsledek: uzavřít spolupráci, nebo nezačne. Řekni jí to.</Oznameni>}
        <label className="block space-y-1.5" htmlFor="nerozhodnuto-datum">
          <span className="block text-[12px] font-bold uppercase tracking-wider text-slate-500">Kdy si zavoláte</span>
          <input id="nerozhodnuto-datum" type="date" value={datum} min={zitra} max={plusDny(dnesIso(), 30)} onChange={(e) => setDatum(e.target.value)}
            className="min-h-[48px] w-full rounded-xl border border-slate-300 bg-white px-3 text-[16px] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-200" />
        </label>
        {pozdeji && <Poznamka id="nerozhodnuto-proc" povinna label={`Proč až ${dmr(datum)}`} value={proc} onChange={setProc} placeholder="Např. je do pátku na dovolené." />}
        <Poznamka id="nerozhodnuto-poznamka" label="Poznámka" value={poznamka} onChange={setPoznamka} placeholder="Např. chce to probrat doma, zavolat po 18:00." />
      </div>
    </Modal>
  );
}

// ─── Nedorazila (Láďa 22. 10. 2026) ─────────────────────────────────────────
// Klientka na zkušební nepřišla. S omluvou: sales jí zavolá a domluví nový termín (kdy zavolat
// si nastaví sama). Bez omluvy: NQ lead, už ji nekontaktujeme. Zkušební zůstane v systému jako
// propadlý (pozdní zrušení). Zapsat jde i zpětně a sales může omluvu později změnit.
export const NEDORAZILA_VOLBY = [
  { key: "omluva", label: "S omluvou", popis: "Omluvila se, třeba i pozdě e-mailem nebo SMS. Sales jí zavolá a domluví nový termín." },
  { key: "bez", label: "Bez omluvy", popis: "Neozvala se. Kontakt jde do NQ lead, už ji nebudeme kontaktovat." },
];
export function NedorazilaDialog({ open, polozka, onClose, onHotovo, uloz: ulozVlastni = null, vychozi = "" }) {
  const [volba, setVolba] = useState(vychozi);
  const [poznamka, setPoznamka] = useState("");
  const [ukladam, setUkladam] = useState(false);
  const [chyba, setChyba] = useState("");
  // 23. 10. 2026: nezaplacený zkušební = faktura se stornuje a vždy NQ lead, omluva se nevybírá.
  const neuhrazeno = !!polozka?.neuhrazeno;
  useEffect(() => { if (open) { setVolba(vychozi); setPoznamka(""); setChyba(""); } }, [open, vychozi]);
  const lzeUlozit = !!volba && (volba !== "omluva" || poznamka.trim().length >= 3);
  const uloz = async () => {
    setUkladam(true); setChyba("");
    try {
      const telo = { zkusebni_id: polozka.id, omluva: volba === "omluva", poznamka };
      const r = ulozVlastni ? await ulozVlastni(telo) : await dealPost("nedorazila", telo);
      // 23. 10. 2026: okno drží „Ukládám…“, dokud rodič nenačte nová data, ať je změna vidět hned po zavření.
      await onHotovo?.(r);
    } catch (e) { setChyba(e.message); } finally { setUkladam(false); }
  };
  return (
    <Modal open={open} onClose={onClose} size="md" plnaHlavicka
      header={<HlavickaOkna ikona={UserMinus} nazev="Nedorazila na zkušební" popis={polozka?.jmeno} onClose={onClose} />}
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose}>Zpět</button>
        <button type="button" className="btn-primary" onClick={uloz} disabled={!lzeUlozit || ukladam}>{ukladam ? "Ukládám…" : "Uložit"}</button>
      </>}>
      <div className="space-y-4">
        {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
        <Oznameni ton="info">Zkušební trénink zůstane v systému jako propadlý (nedorazila). Připomínky výsledku zkušebního se zastaví.</Oznameni>
        {neuhrazeno && <Oznameni ton="problem"><strong>Zkušební zatím není zaplacený.</strong> Pokud se platba neobjeví ani teď, faktura se stornuje a kontakt půjde do NQ lead, i když se omluvila.</Oznameni>}
        <Volby name="Omluvila se?" moznosti={NEDORAZILA_VOLBY} value={volba} onChange={setVolba} />
        {volba === "bez" && <Oznameni ton="problem"><strong>Kontakt půjde do NQ lead.</strong> Kdyby se později omluvila, sales to v jejím kontaktu změní na S omluvou.</Oznameni>}
        <Poznamka id="nedorazila-poznamka" povinna={volba === "omluva"} label={volba === "omluva" ? "Jak se omluvila" : "Poznámka"} value={poznamka} onChange={setPoznamka}
          placeholder={volba === "omluva" ? "Např. hodinu po tréninku napsala e-mail, že je nemocná." : "Např. nebrala telefon, nepřišla."} />
      </div>
    </Modal>
  );
}

// ─── Uzavřít spolupráci: průvodce ────────────────────────────────────────────
// 4. 11. 2026 (schválené mockupy 1/3 Shrnutí, 2/3 Údaje, 3/3 Podpis, 3b na dálku): smlouva je vždy pro
// vybraný balíček a podepisuje se na konci. Po podpisu se spolupráce uzavře sama.
const KROKY = ["Balíček", "První trénink", "Údaje", "Shrnutí", "Podpis"];

function Kroky({ krok }) {
  return (
    <div className="mb-4 space-y-2">
      <div className="grid grid-cols-5 gap-2">
        {KROKY.map((k, i) => <span key={k} className={`h-1.5 rounded-full ${i < krok ? "bg-brand/70" : i === krok ? "bg-brand" : "bg-slate-200"}`} />)}
      </div>
      <div className="hidden grid-cols-5 gap-2 sm:grid">
        {KROKY.map((k, i) => <span key={k} className={`text-[13px] font-bold ${i === krok ? "text-brand-deep" : "text-slate-500"}`}>{i + 1}. {k}</span>)}
      </div>
      <div className="text-[14px] font-bold text-brand-deep sm:hidden">Krok {krok + 1} z 5: {KROKY[krok]}</div>
    </div>
  );
}

function popisBalicku(b) {
  if (b.typ === "PERMANENTKA") return `${kc(b.cena)} jednorázově, 10 tréninků na 4 měsíce`;
  const tydne = b.treninku_tydne === 1 ? "1 trénink týdně" : `${b.treninku_tydne} tréninky týdně`;
  if (b.typ === "FLEXIBLE") return `${kc(b.cena)} měsíčně, bez závazku, ${tydne}`;
  return `${kc(b.cena)} měsíčně, ${b.typ === "PREMIUM" ? "48" : "24"} týdnů, ${tydne}`;
}

function Pole({ id, label, value, onChange, typ = "text", autoComplete }) {
  return (
    <label className="block space-y-1" htmlFor={id}>
      <span className="block text-[13px] font-semibold text-slate-600">{label}</span>
      <input id={id} type={typ} value={value || ""} autoComplete={autoComplete} onChange={(e) => onChange(e.target.value)}
        className="min-h-[46px] w-full rounded-xl border border-slate-300 bg-white px-3 text-[16px] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-200" />
    </label>
  );
}

// Adresa bývá dlouhá: pilulka, která se smí zalomit (na telefonu by jinak přetekla).
function PilZalom({ popisek, children, ton = "bg-slate-100 text-slate-700" }) {
  return (
    <span className={`inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-[13px] font-bold ${ton}`}>
      {popisek}<span aria-hidden className="h-3 w-px shrink-0 bg-current opacity-40" /><span className="min-w-0 break-words">{children}</span>
    </span>
  );
}

const PODPIS_API = "/api/smlouva-podpis";
async function podpisApi(metoda, data) {
  const r = metoda === "GET" ? await apiFetch(`${PODPIS_API}?${new URLSearchParams(data).toString()}`) : await apiFetch(PODPIS_API, { method: "POST", body: JSON.stringify(data) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || "Něco se nepovedlo.");
  return j;
}
const adresaText = (a) => (a && a.ulice && a.mesto && a.psc ? `${a.ulice}, ${a.mesto}, ${a.psc}` : "");
const udajeKompletni = (u) => !!(u && String(u.jmeno || "").trim() && /@/.test(u.email || "") && String(u.telefon || "").replace(/\D/g, "").length >= 9 && adresaText(u.adresa));
const firmaKompletni = (f) => !!(f && f.firma && f.ico && f.ulice && f.mesto && f.psc);
// Fakturace: na osobu = adresa klientky (stejná jako ve smlouvě), na firmu = údaje firmy.
const fakturaceZUdaju = (u, f) => (f?.typ === "firma"
  ? { typ: "firma", firma: f.firma || "", ico: f.ico || "", dic: f.dic || "", ulice: f.ulice || "", mesto: f.mesto || "", psc: f.psc || "" }
  : { typ: "osoba", ulice: u?.adresa?.ulice || "", mesto: u?.adresa?.mesto || "", psc: u?.adresa?.psc || "" });

// Krok 3: údaje klientky do smlouvy i na fakturu (schválený mockup 2/3).
function UdajeKrok({ d, u, setU, f, setF, volba, setVolba, poznamka, setPoznamka }) {
  const nastav = (k) => (v) => setU((x) => ({ ...x, [k]: v }));
  const nastavA = (k) => (v) => setU((x) => ({ ...x, adresa: { ...(x.adresa || {}), [k]: v } }));
  const nastavF = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const muz = d.muz;
  if (!u) return <div className="flex items-center gap-2 py-6 text-[14px] text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Načítám údaje…</div>;
  return (
    <>
      <CoRict>Zkontrolujme spolu vaše údaje. Půjdou do smlouvy i na fakturu.</CoRict>
      <div className="rounded-2xl border border-slate-200 p-4 sm:p-5">
        <div className="mb-3 text-[15px] font-bold text-slate-900">Údaje {muz ? "klienta" : "klientky"}</div>
        <div className="flex flex-wrap gap-2">
          <PilZalom popisek="Jméno">{u.jmeno || "chybí"}</PilZalom>
          <PilZalom popisek="E‑mail">{u.email || "chybí"}</PilZalom>
          <PilZalom popisek="Telefon">{u.telefon || "chybí"}</PilZalom>
          <PilZalom popisek="Adresa">{adresaText(u.adresa) || "chybí"}</PilZalom>
          <PilZalom popisek="Fakturovat na" ton="bg-brand-100 text-brand-deep">{f?.typ === "firma" ? `firmu${f.firma ? ` ${f.firma}` : ""}${f.ico ? `, IČO ${f.ico}` : ""}` : "osobu"}</PilZalom>
        </div>
        <p className="mt-3 text-[13px] text-slate-500">Na telefon přijde SMS k podpisu, na e‑mail podepsaná smlouva a faktury.</p>
      </div>
      <Oznameni ton="info">Splatnost faktury je vždy <strong>3 dny</strong>. {muz ? "Klienta" : "Klientku"} k delší splatnosti nepobízej. Když ji {muz ? "sám" : "sama"} chce, je to výjimka ke schválení, ať potom napíše na <strong>klienti@zateckal.cz</strong>.</Oznameni>
      <Poznamka id="deal-poznamka" label="Poznámka k uzavření (nepovinné)" value={poznamka} onChange={setPoznamka} placeholder="Např. nastupuje později, výjimka v ceně." />
      <Volby name="Údaje" value={volba} onChange={setVolba} moznosti={[
        ...(udajeKompletni(u) ? [{ key: "sedi", label: "Údaje sedí", popis: "Pokračuje rovnou na Shrnutí" }] : []),
        { key: "upravit", label: "Upravit nebo fakturovat na firmu", popis: "Při fakturaci na firmu zůstane ve smlouvě osobní adresa klientky" },
      ]} />
      {volba === "upravit" && (
        <div className="space-y-3 rounded-2xl border border-slate-200 p-3 sm:p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Pole id="u-jmeno" label="Jméno a příjmení" value={u.jmeno} onChange={nastav("jmeno")} autoComplete="name" />
            <Pole id="u-tel" label="Telefon (přijde na něj SMS k podpisu)" typ="tel" value={u.telefon} onChange={nastav("telefon")} autoComplete="tel" />
          </div>
          <Pole id="u-email" label="E‑mail (smlouva a faktury)" typ="email" value={u.email} onChange={nastav("email")} autoComplete="email" />
          <div className="grid gap-3 sm:grid-cols-[2fr_1.4fr_1fr]">
            <Pole id="u-ulice" label="Ulice a číslo" value={u.adresa?.ulice} onChange={nastavA("ulice")} autoComplete="street-address" />
            <Pole id="u-mesto" label="Město" value={u.adresa?.mesto} onChange={nastavA("mesto")} autoComplete="address-level2" />
            <Pole id="u-psc" label="PSČ" value={u.adresa?.psc} onChange={nastavA("psc")} autoComplete="postal-code" />
          </div>
          <div className="space-y-2 border-t border-slate-100 pt-3">
            <span className="block text-[13px] font-semibold text-slate-600">Fakturovat na</span>
            <Cipy moznosti={[{ key: "osoba", label: "Na osobu" }, { key: "firma", label: "Na firmu" }]} value={f?.typ || "osoba"} onChange={(v) => setF((x) => ({ ...x, typ: v || "osoba" }))} />
          </div>
          {f?.typ === "firma" && (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <Pole id="f-firma" label="Název firmy" value={f.firma} onChange={nastavF("firma")} autoComplete="organization" />
                <Pole id="f-ico" label="IČO" value={f.ico} onChange={nastavF("ico")} />
                <Pole id="f-dic" label="DIČ (nepovinné)" value={f.dic} onChange={nastavF("dic")} />
              </div>
              <div className="grid gap-3 sm:grid-cols-[2fr_1.4fr_1fr]">
                <Pole id="f-ulice" label="Ulice a číslo firmy" value={f.ulice} onChange={nastavF("ulice")} />
                <Pole id="f-mesto" label="Město firmy" value={f.mesto} onChange={nastavF("mesto")} />
                <Pole id="f-psc" label="PSČ firmy" value={f.psc} onChange={nastavF("psc")} />
              </div>
            </>
          )}
          <p className="text-[13px] text-slate-500">Úprava se uloží i do kontaktu {muz ? "klienta" : "klientky"}.</p>
        </div>
      )}
    </>
  );
}

function StavRadek({ hotovo, aktivni, children }) {
  return (
    <div className="flex items-center gap-2.5 text-[14px]">
      {hotovo ? <CircleCheck className="h-5 w-5 shrink-0 text-emerald-600" /> : aktivni ? <Clock className="h-5 w-5 shrink-0 text-brand" /> : <span className="ml-0.5 h-4 w-4 shrink-0 rounded-full border-2 border-slate-300" />}
      <span className={hotovo ? "font-bold text-slate-900" : aktivni ? "font-bold text-brand-deep" : "text-slate-500"}>{children}</span>
    </div>
  );
}

// „zítra 11:24“, „dnes 18:00“, jinak „čt 5. 11. 9:00“.
function kdyKratce(iso) {
  if (!iso) return "";
  const dt = new Date(iso);
  const den = dt.toLocaleDateString("sv-SE", { timeZone: "Europe/Prague" });
  const hm = dt.toLocaleTimeString("cs-CZ", { timeZone: "Europe/Prague", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).replace(/^0(\d)/, "$1");
  const dnes = dnesIso();
  if (den === dnes) return `dnes ${hm}`;
  if (den === plusDny(dnes, 1)) return `zítra ${hm}`;
  return `${DNY_KRATCE[new Date(`${den}T12:00:00Z`).getUTCDay()]} ${dm(den)} ${hm}`;
}
const terminKratce = (p) => (p?.datum ? `${DNY_KRATCE[new Date(`${p.datum}T12:00:00Z`).getUTCDay()]} ${dm(p.datum)} ${cas(p.cas)}` : "");

function PilulkySmlouvy({ smlouva, vybrany }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Pil ton="znacka" casti={["Smlouva", `${smlouva?.nazev || "Komplexní koučink"}, ${vybrany?.kratce || ""}`]} />
      {vybrany && <Pil ton="neutralni" casti={[kc(vybrany.cena), vybrany.typ === "PERMANENTKA" ? "jednorázově" : "měsíčně"]} />}
    </div>
  );
}

// Krok 5 na místě (schválený mockup 3/3): QR kód, po podpisu se spolupráce uzavře sama.
// Klientka bez telefonu: skrytá možnost dole, odkaz e‑mailem, otevře ho na tabletu.
function PodpisNaMiste({ zkusebniId, d, u, smlouva, vybrany, onPodepsano }) {
  const [bezTelefonu, setBezTelefonu] = useState(false);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState("");
  const [chyba, setChyba] = useState("");
  const poslatEmail = async () => {
    setBusy(true); setChyba(""); setInfo("");
    try { const r = await podpisApi("POST", { akce: "odkaz_emailem", zkusebni_id: zkusebniId }); setInfo(r.test_odkaz ? `Zkušební odkaz: ${r.test_odkaz}` : `Odkaz odešel na ${u?.email || "e‑mail klientky"}. Otevřete ho na tabletu.`); }
    catch (e) { setChyba(e.message); } finally { setBusy(false); }
  };
  return (
    <>
      <CoRict>Teď spolu podepíšeme smlouvu. Na tabletu si ji v klidu přečtěte, podpis pak potvrdíte ve svém telefonu přes SMS.</CoRict>
      <PilulkySmlouvy smlouva={smlouva} vybrany={vybrany} />
      <PodpisQr zkusebniId={zkusebniId} bezKontroly onPodepsano={onPodepsano} />
      <Oznameni ton="znacka" ikona={CircleCheck}><strong>Jakmile {d.muz ? "klient podepíše" : "klientka podepíše"}, spolupráce se uzavře sama.</strong> Uvidíš tu hotovo, faktura odejde {d.muz ? "klientovi" : "klientce"}.</Oznameni>
      <div className="border-t border-slate-100 pt-3">
        {!bezTelefonu ? (
          <button type="button" className="text-[14px] font-semibold text-slate-500 underline underline-offset-2" onClick={() => setBezTelefonu(true)}>Nemá u sebe telefon?</button>
        ) : (
          <div className="space-y-2">
            <p className="text-[14px] text-slate-600"><strong>Nejdřív ať si ho {d.muz ? "klient donese" : "klientka donese"} ze šatny.</strong> Když to nejde, pošli {d.muz ? "mu" : "jí"} odkaz e‑mailem{u?.email ? <> na <strong className="break-all">{u.email}</strong></> : null}. Otevře ho na tabletu a podepíše prstem přímo tam.</p>
            <button type="button" className="btn-secondary w-full justify-center sm:w-auto" onClick={poslatEmail} disabled={busy}><Mail className="h-4 w-4" />{busy ? "Posílám…" : "Poslat odkaz e‑mailem"}</button>
            {info && <Oznameni ton="ok">{info}</Oznameni>}
            {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
          </div>
        )}
      </div>
    </>
  );
}

// Krok 5 po telefonu (schválený mockup 3b): odkaz SMS a e‑mailem na 24 hodin, první trénink zablokovaný.
function PodpisNaDalku({ zkusebniId, d, smlouva, vybrany, prvni, priprava, onZnovu, onPodepsano }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  const [info, setInfo] = useState("");
  const hotovoRef = React.useRef(false);
  useEffect(() => {
    let zive = true;
    const nacti = () => dealGet("uzavreni_stav", { zkusebni_id: zkusebniId }).then((r) => {
      if (!zive) return;
      setSt(r);
      if (r.podpis?.stav === "podepsano" && !hotovoRef.current) { hotovoRef.current = true; onPodepsano?.(); }
    }).catch(() => {});
    nacti();
    const id = setInterval(() => { if (document.visibilityState === "visible") nacti(); }, 4000);
    return () => { zive = false; clearInterval(id); };
  }, [zkusebniId, priprava]); // eslint-disable-line react-hooks/exhaustive-deps
  const p = st?.podpis;
  const platiDo = st?.uzavreni?.plati_do || priprava?.plati_do || null;
  const vyprselo = platiDo && new Date(platiDo).getTime() < Date.now();
  const znovu = async () => {
    setBusy(true); setChyba(""); setInfo("");
    try { await onZnovu(); setInfo("Nový odkaz odešel."); } catch (e) { setChyba(e.message); } finally { setBusy(false); }
  };
  const kanaly = p ? [!p.sms_chyba && p.sms ? "SMS" : null, p.email_odkaz || p.dalku_email ? "e‑mailem" : null].filter(Boolean).join(" a ") : "";
  return (
    <>
      <CoRict>Smlouvu vám teď posílám do telefonu SMS i e‑mailem. Můžete si ji projít a rovnou podepsat, já počkám.</CoRict>
      <PilulkySmlouvy smlouva={smlouva} vybrany={vybrany} />
      <div className="space-y-3 rounded-2xl border border-slate-200 p-4 sm:p-5">
        <div className="flex items-center gap-2 text-[15px] font-bold text-slate-900"><Smartphone className="h-5 w-5 text-brand" />Odkaz k podpisu má {d.muz ? "klient" : "klientka"} v telefonu</div>
        {!p ? <div className="flex items-center gap-2 text-[14px] text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Načítám stav…</div> : (
          <div className="space-y-2.5">
            <StavRadek hotovo>Odešel {kanaly || "klientce"}{p.sms ? ` v ${p.sms}` : ""}</StavRadek>
            <StavRadek hotovo={!!p.tel_otevreno} aktivni={!p.tel_otevreno}>{p.tel_otevreno ? `${d.muz ? "Klient" : "Klientka"} odkaz ${d.muz ? "otevřel" : "otevřela"} v ${p.tel_otevreno}` : `${d.muz ? "Klient" : "Klientka"} ho zatím ${d.muz ? "neotevřel" : "neotevřela"}`}</StavRadek>
            <StavRadek aktivni={!vyprselo}>{vyprselo ? "Odkaz vypršel" : "Čekáme na podpis"}</StavRadek>
          </div>
        )}
        {p?.sms_chyba && <Oznameni ton="pozor">SMS se nepodařilo poslat, odkaz odešel jen e‑mailem.</Oznameni>}
        <div className="flex flex-wrap gap-2">
          {platiDo && <Pil ton={vyprselo ? "problem" : "pozor"} ikona={Clock} casti={[vyprselo ? "odkaz vypršel" : "odkaz platí do", kdyKratce(platiDo)]} />}
          {prvni && <Pil ton="neutralni" ikona={CalendarClock} casti={["první trénink", `${terminKratce(prvni)}${vyprselo ? "" : ", blokovaný"}`]} />}
        </div>
        {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
        {info && <Oznameni ton="ok">{info}</Oznameni>}
        <button type="button" className="btn-secondary min-h-[52px] w-full justify-center text-base" onClick={znovu} disabled={busy}><Send className="h-5 w-5" />{busy ? "Posílám…" : "Poslat odkaz znovu"}</button>
      </div>
      <Oznameni ton="znacka" ikona={CircleCheck}><strong>Faktura, první trénink a uvítací e‑mail se vystaví, až {d.muz ? "klient podepíše" : "klientka podepíše"}.</strong> Okno můžeš zavřít, uzavření proběhne samo.</Oznameni>
      <Oznameni ton="info">Když do 24 hodin {d.muz ? "nepodepíše" : "nepodepíše"}, blokovaný termín se uvolní a přijde ti úkol {d.muz ? "mu" : "jí"} zavolat.</Oznameni>
    </>
  );
}

// Po podpisu: uzavření spolupráce (běží na serveru, tady se spustí a čeká se na výsledek).
function UzavreniPoPodpisu({ zkusebniId, d, onHotovo, onRucne }) {
  const [chyba, setChyba] = useState(null); // { text, kroky }
  const [pokus, setPokus] = useState(0);
  useEffect(() => {
    let zive = true, casovac = null;
    const vysledek = (stav, v) => {
      if (!zive) return true;
      if (stav === "hotovo" && v) { onHotovo(v); return true; }
      if (stav === "chyba") { setChyba({ text: v?.chyba || "Uzavření se nepovedlo.", kroky: v?.kroky || [] }); return true; }
      return false;
    };
    let kol = 0;
    const cekej = () => {
      casovac = setTimeout(() => {
        // Každých asi 75 s znovu požádat o spuštění (server pustí jen zaseknuté uzavření).
        if (++kol % 30 === 0) dealPost("dokoncit_po_podpisu", { zkusebni_id: zkusebniId }).catch(() => {});
        dealGet("uzavreni_stav", { zkusebni_id: zkusebniId })
          .then((r) => { if (!vysledek(r.uzavreni?.stav, r.uzavreni?.vysledek)) cekej(); })
          .catch(() => cekej());
      }, 2500);
    };
    setChyba(null);
    dealPost("dokoncit_po_podpisu", { zkusebni_id: zkusebniId })
      .then((r) => { if (!vysledek(r.stav, r.vysledek)) cekej(); })
      .catch((e) => { if (zive) setChyba({ text: e.message, kroky: e.kroky || [] }); });
    return () => { zive = false; if (casovac) clearTimeout(casovac); };
  }, [zkusebniId, pokus]); // eslint-disable-line react-hooks/exhaustive-deps
  if (chyba) return (
    <div className="space-y-3">
      <Oznameni ton="ok"><strong>Smlouva je podepsaná.</strong></Oznameni>
      <Oznameni ton="problem"><strong>Spolupráce se zatím neuzavřela.</strong> {chyba.text} Láďa o tom ví.</Oznameni>
      {chyba.kroky.filter((k) => k.ok).length > 0 && <div className="rounded-2xl border border-slate-200 p-4"><Fajfky polozky={chyba.kroky.filter((k) => k.ok).map((k) => tucne(k.text))} /></div>}
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-secondary" onClick={() => setPokus((x) => x + 1)}><RefreshCw className="h-4 w-4" />Zkusit dokončit znovu</button>
        {onRucne && <button type="button" className="btn-secondary" onClick={onRucne}><Handshake className="h-4 w-4" />Projít údaje a uzavřít</button>}
      </div>
    </div>
  );
  return (
    <div className="space-y-4 py-4 text-center">
      <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><CircleCheck className="h-9 w-9" /></span>
      <h3 className="font-serif text-2xl font-bold text-slate-900">Smlouva je podepsaná</h3>
      <p className="flex items-center justify-center gap-2 text-[16px] text-slate-700"><Loader2 className="h-5 w-5 animate-spin text-brand" />Uzavírám spolupráci, vystavuji fakturu…</p>
      <p className="text-[13px] text-slate-500">{d.muz ? "Klient" : "Klientka"} už nemusí nic dělat.</p>
    </div>
  );
}

export function DealPruvodce({ open, polozka, onClose, onHotovo, onZmena = null, zpetText = "Zpět na přehled" }) {
  const [d, setD] = useState(null);
  const [chyba, setChyba] = useState("");
  const [krok, setKrok] = useState(0);
  const [balicek, setBalicek] = useState("");
  const [prvni, setPrvni] = useState(null);
  const [u, setU] = useState(null);            // údaje klientky do smlouvy (jméno, e‑mail, telefon, adresa)
  const [volba, setVolba] = useState("");      // Údaje sedí | upravit
  const [f, setF] = useState({ typ: "osoba" }); // fakturace (na firmu: údaje firmy)
  const [poznamka, setPoznamka] = useState("");
  const [nahled, setNahled] = useState(null);
  const [nacitamNahled, setNacitamNahled] = useState(false);
  const [odesilam, setOdesilam] = useState(false);
  const [priprava, setPriprava] = useState(null); // výsledek pripravit_podpis (krok Podpis)
  const [podepsano, setPodepsano] = useState(false);
  const [hotovo, setHotovo] = useState(null);
  const [obnoveno, setObnoveno] = useState(false);

  useEffect(() => {
    if (!open || !polozka) return;
    setObnoveno(false);
    setD(null); setChyba(""); setKrok(0); setBalicek(""); setPrvni(null); setU(null); setVolba(""); setF({ typ: "osoba" }); setPoznamka(""); setNahled(null); setPriprava(null); setPodepsano(false); setHotovo(null);
    Promise.all([dealGet("detail", { zkusebni_id: polozka.id }), podpisApi("GET", { akce: "udaje", zkusebni_id: polozka.id }).catch(() => null)])
      .then(([x, ud]) => {
        setD(x);
        if (x.fakturace?.typ === "firma") setF(x.fakturace);
        if (ud?.udaje) { setU(ud.udaje); setVolba(udajeKompletni(ud.udaje) && !ud.adresa_chybi && x.fakturace?.typ !== "firma" ? "" : "upravit"); }
        else setU({ jmeno: x.jmeno || "", email: x.email || "", telefon: x.telefon || "", adresa: { ulice: "", mesto: "", psc: "" } });
        // Připravený podpis (trenér zavřel okno, klientka podepisuje na dálku): rovnou krok Podpis.
        const pr = x.pripraveno;
        if (pr?.vstup) {
          setBalicek(pr.vstup.balicek || ""); setPrvni(pr.vstup.prvni || null); setPoznamka(pr.vstup.poznamka || "");
          if (pr.vstup.fakturace?.typ === "firma") setF(pr.vstup.fakturace);
          setVolba("sedi");
          // Na místě nepodepsaná smlouva: zpět na Shrnutí, ať se údaje ověří znovu (nový QR až po Podepsat smlouvu).
          if (pr.rezim === "tablet" && !pr.podepsano) { setKrok(3); return; }
          setPriprava({ rezim: pr.rezim, plati_do: pr.plati_do }); setPodepsano(!!pr.podepsano); setKrok(4);
          return;
        }
        // Rozpracované uzavření (např. vypadl signál nebo selhal krok): stejné údaje se předvyplní, server pokračuje tam, kde skončil.
        const r = x.rozpracovano;
        if (r) { if (r.balicek) setBalicek(r.balicek); if (r.prvni) setPrvni(r.prvni); return; }
        if (x.podepsano?.balicek) setBalicek(x.podepsano.balicek);
        // Koncept v zařízení (vypadl internet, zavřel okno, prohlížeč obnovil stránku): pokračuje, kde skončil.
        const k = konceptNacti(polozka.id);
        if (k) {
          const balOk = k.balicek && (x.balicky || []).some((b) => b.balicek === k.balicek) && (!x.podepsano?.balicek || x.podepsano.balicek === k.balicek);
          const prvOk = !!(k.prvni?.datum > dnesIso() && k.prvni?.cas);
          if (balOk) setBalicek(k.balicek);
          if (prvOk) setPrvni(k.prvni);
          if (k.poznamka) setPoznamka(k.poznamka);
          setKrok(Math.min(Number(k.krok) || 0, 2, !balOk ? 0 : !prvOk ? 1 : 2));
          setObnoveno(true);
        }
      })
      .catch((e) => setChyba(e.message));
  }, [open, polozka]);

  useEffect(() => {
    // Uložit jen koncept právě načtené klientky (při přepnutí na jinou drží stav chvíli ještě předchozí).
    if (!open || !polozka || !d || hotovo || priprava || d.rozpracovano || String(d.zkusebni?.id) !== String(polozka.id)) return;
    konceptUloz(polozka.id, { krok: Math.min(krok, 2), balicek, prvni, poznamka });
  }, [open, polozka, d, hotovo, priprava, krok, balicek, prvni, poznamka]);

  // Jak se smlouva podepíše: na místě (tablet), po telefonu (na dálku), už podepsaná, nebo dokončení starého uzavření.
  const rezim = !d ? null
    : d.rozpracovano?.smlouva === "na_prvnim" ? "na_prvnim"
      : d.rozpracovano?.smlouva === "podepsana" || d.podepsano ? "podepsana"
        : d.nerozhodnuto ? "na_dalku" : "tablet";
  const priprava2 = rezim === "tablet" || rezim === "na_dalku";
  const vybrany = useMemo(() => (d?.balicky || []).find((b) => b.balicek === balicek) || null, [d, balicek]);
  const fakturace = useMemo(() => fakturaceZUdaju(u, f), [u, f]);
  const vstup = useMemo(() => ({ zkusebni_id: polozka?.id, smlouva: rezim, balicek, prvni, fakturace, email: String(u?.email || "").trim(), poznamka }), [polozka, rezim, balicek, prvni, fakturace, u, poznamka]);

  // Krok 4: náhled ze serveru (stejný výpočet jako uzavření, nic se nezapisuje).
  useEffect(() => {
    if (krok !== 3) return;
    setNacitamNahled(true); setNahled(null); setChyba("");
    dealPost("nahled", vstup).then(setNahled).catch((e) => setChyba(e.message)).finally(() => setNacitamNahled(false));
  }, [krok]); // eslint-disable-line react-hooks/exhaustive-deps

  const udajeOk = udajeKompletni(u) && (f?.typ !== "firma" || firmaKompletni(f)) && !!volba;
  const lzeDal = [!!vybrany, !!(prvni?.datum && prvni?.cas), udajeOk, !!nahled?.lze_vystavit][krok];

  // Údaje → Shrnutí: údaje do smlouvy se uloží do kontaktu (stejně jako dřív v kroku Smlouva).
  const dalZUdaju = async () => {
    setOdesilam(true); setChyba("");
    try { const r = await podpisApi("POST", { akce: "udaje", zkusebni_id: polozka.id, udaje: u }); setU(r.udaje); setKrok(3); }
    catch (e) { setChyba(e.message); } finally { setOdesilam(false); }
  };
  const dal = () => (krok === 2 ? dalZUdaju() : setKrok(krok + 1));
  // Láďa 4. 11. 2026: klepnutí na Údaje sedí rovnou uloží údaje a přejde na Shrnutí (Zpět vrací na Údaje).
  const zvolUdaje = (v) => {
    if (v === "sedi" && f?.typ === "firma" && !firmaKompletni(f)) { setVolba("upravit"); setChyba("Doplň údaje firmy, pak klepni na Údaje sedí."); return; }
    setVolba(v);
    if (v === "sedi" && !odesilam && udajeKompletni(u)) dalZUdaju();
  };

  // Shrnutí → Podepsat smlouvu (na místě) / Poslat smlouvu k podpisu (po telefonu).
  const pripravit = async (znovu = false) => {
    if (!nahled?.klic && !znovu) return;
    setOdesilam(true); setChyba("");
    try {
      let klic = nahled?.klic;
      if (znovu && !klic) klic = (await dealPost("nahled", vstup)).klic;
      const r = await dealPost("pripravit_podpis", { ...vstup, klic, znovu });
      konceptSmaz(polozka.id);
      setPriprava(r); setKrok(4); onZmena?.();
      if (r.podepsano) setPodepsano(true);
      return r;
    } catch (e) { if (znovu) throw e; setChyba(e.message); return null; }
    finally { setOdesilam(false); }
  };

  // Smlouva už podepsaná (nebo dokončení starého uzavření): uzavřít hned.
  const uzavrit = async () => {
    if (!nahled?.klic || odesilam) return;
    const ok = await confirmDialog({
      title: "Uzavřít spolupráci?",
      description: `Klientce ${d.jmeno} se teď vystaví a pošle faktura ${kc(vybrany.cena)} (${vybrany.kratce}) z účtu Firma, splatnost 3 dny. Opravdu uzavřít?`,
      confirmLabel: "Ano, uzavřít a vystavit fakturu", cancelLabel: "Zpět", variant: "primary",
    });
    if (!ok) return;
    setOdesilam(true); setChyba("");
    try { const r = await dealPost("uzavrit", { ...vstup, klic: nahled.klic }); konceptSmaz(polozka.id); setHotovo(r); onHotovo?.(r); }
    catch (e) { setChyba(e.message); if (e.kroky) { konceptSmaz(polozka.id); setHotovo({ chyba: e.message, kroky: e.kroky }); onZmena?.(); } }
    finally { setOdesilam(false); }
  };

  const popis = d ? `${d.jmeno}, ${kdyZkusebni(d.zkusebni)} ${sTrenerem(d.trener?.jmeno)}` : polozka?.jmeno;
  const header = <HlavickaOkna ikona={Handshake} nazev="Uzavřít spolupráci" popis={popis} onClose={onClose} />;

  if (hotovo && !hotovo.chyba) {
    const h = hotovo.hotovo || {};
    const ok = (hotovo.kroky || []).filter((k) => k.ok);
    const spatne = (hotovo.kroky || []).filter((k) => !k.ok);
    return (
      <Modal open={open} onClose={onClose} size="lg" plne plnaHlavicka header={header}
        footer={<button type="button" className="btn-primary" onClick={onClose}>{zpetText}</button>}>
        <div className="mx-auto max-w-xl space-y-4 py-2 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><CircleCheck className="h-9 w-9" /></span>
          <h3 className="font-serif text-2xl font-bold text-slate-900">Spolupráce uzavřena</h3>
          <p className="text-[16px] text-slate-700"><strong>{h.jmeno}</strong>, {h.balicek}, první trénink <strong>{h.prvni ? `${DNY_KRATCE[new Date(`${h.prvni.datum}T12:00:00Z`).getUTCDay()]} ${dm(h.prvni.datum)} v ${cas(h.prvni.cas)}` : ""}</strong></p>
          <div className="rounded-2xl border border-slate-200 p-4 text-left"><Fajfky polozky={ok.map((k) => tucne(k.text))} /></div>
          {spatne.length > 0 && (
            <div className="space-y-2 text-left">{spatne.map((k, i) => <Oznameni key={i} ton="pozor">{tucne(k.text)}</Oznameni>)}</div>
          )}
          <MereniInBody />
          <DalsiTreninky hotovo />
        </div>
      </Modal>
    );
  }

  const naDalku = (priprava?.rezim || rezim) === "na_dalku";
  const footer = krok === 4 ? (
    podepsano ? <button type="button" className="btn-secondary" onClick={onClose}>Zavřít</button> : naDalku
      ? <><button type="button" className="btn-secondary" onClick={() => setKrok(3)}><ChevronLeft className="h-4 w-4" />Zpět</button><button type="button" className="btn-primary" onClick={onClose}>Zavřít</button></>
      : <button type="button" className="btn-secondary" onClick={() => setKrok(3)}><ChevronLeft className="h-4 w-4" />Zpět</button>
  ) : (
    <>
      <button type="button" className="btn-secondary" onClick={krok === 0 ? onClose : () => setKrok(krok - 1)} disabled={odesilam}>
        <ChevronLeft className="h-4 w-4" />{krok === 0 ? "Zavřít" : krok === 3 ? "Zpět na údaje" : "Zpět"}
      </button>
      {krok < 3
        ? <button type="button" className="btn-primary" disabled={!lzeDal || odesilam} onClick={dal}>{odesilam ? "Ukládám…" : "Pokračovat"}<ChevronRight className="h-4 w-4" /></button>
        : priprava2
          ? <button type="button" className="btn-primary !bg-emerald-600 hover:!bg-emerald-700" disabled={!lzeDal || odesilam} onClick={() => pripravit(false)}>{naDalku ? <Send className="h-4 w-4" /> : <FilePenLine className="h-4 w-4" />}{odesilam ? "Připravuji…" : naDalku ? "Poslat smlouvu k podpisu" : "Podepsat smlouvu"}</button>
          : <button type="button" className="btn-primary !bg-emerald-600 hover:!bg-emerald-700" disabled={!lzeDal || odesilam} onClick={uzavrit}><Handshake className="h-4 w-4" />{odesilam ? "Uzavírám…" : "Uzavřít spolupráci"}</button>}
    </>
  );

  return (
    <Modal open={open} onClose={onClose} size="lg" plne plnaHlavicka header={header} footer={footer}>
      <Kroky krok={krok} />
      {chyba && <Oznameni ton="problem" className="mb-3">{chyba}</Oznameni>}
      {hotovo?.chyba && <div className="mb-3 space-y-2"><Fajfky polozky={(hotovo.kroky || []).filter((k) => k.ok).map((k) => tucne(k.text))} /></div>}
      {!d ? (!chyba && <div className="py-10 text-center text-slate-500">Načítám…</div>) : (
        <div className="space-y-4">
          {obnoveno && !d.rozpracovano && !hotovo && krok < 3 && <Oznameni ton="znacka"><strong>Pokračuješ, kde jsi skončil.</strong> Vyplněné údaje zůstaly uložené.</Oznameni>}
          {d.rozpracovano && !hotovo && krok < 4 && <Oznameni ton="pozor"><strong>Uzavření se minule nedokončilo.</strong> Údaje jsou předvyplněné, projdi je a dokonči. Co už proběhlo, se nezopakuje.</Oznameni>}
          {rezim === "podepsana" && krok < 4 && <Oznameni ton="ok"><strong>Smlouva je podepsaná.</strong> Projdi údaje a uzavři spolupráci.</Oznameni>}
          {rezim === "na_prvnim" && krok < 4 && <Oznameni ton="znacka" ikona={FilePenLine}>Tohle uzavření začalo postaru, smlouvu {d.muz ? "klient podepíše" : "klientka podepíše"} na prvním tréninku.</Oznameni>}
          {krok === 0 && (
            <>
              <CoRict>Jaký balíček si {d.muz ? "vybral" : "vybrala"}?</CoRict>
              <Volby name="Balíček" value={balicek} onChange={setBalicek} moznosti={d.balicky.map((b) => ({ key: b.balicek, label: b.kratce, popis: popisBalicku(b) }))} />
              <p className="text-[13px] text-slate-500">Ceny jsou z produktů ve FAPI, fakturuje se z účtu Firma. Smlouva bude pro vybraný balíček.</p>
            </>
          )}
          {krok === 1 && (
            <>
              <CoRict>Kdy bude první trénink? Vyber volný čas ve svém kalendáři.</CoRict>
              <DalsiTreninky />
              <PrvniTrenink predbezne={{ trener_id: d.trener.id, trener: d.trener.jmeno, casy: [], putujici: null }} lokalita={d.zkusebni.lokalita}
                value={prvni} onChange={setPrvni} nacti={({ mesic }) => dealGet("terminy", { zkusebni_id: polozka.id, mesic })} />
            </>
          )}
          {krok === 2 && <UdajeKrok d={d} u={u} setU={setU} f={f} setF={setF} volba={volba} setVolba={zvolUdaje} poznamka={poznamka} setPoznamka={setPoznamka} />}
          {krok === 3 && (
            nacitamNahled ? <div className="py-10 text-center text-slate-500">Počítám shrnutí…</div> : nahled && (
              <>
                <CoRict>{rezim === "na_dalku" ? "Shrnu vám, co jsme domluvili. Pak vám pošlu smlouvu do telefonu SMS i e‑mailem, můžete ji podepsat hned při hovoru."
                  : rezim === "tablet" ? "Shrnu vám, co jsme domluvili. Pak spolu podepíšeme smlouvu, přečtete si ji na tabletu a podpis potvrdíte ve svém telefonu."
                    : "Shrnu vám, co jsme domluvili."}</CoRict>
                {!nahled.lze_vystavit && (
                  <Oznameni ton="problem" ikona={AlertTriangle}><strong>Fakturu teď nejde vystavit.</strong> {nahled.blokace.join(" ")} Napiš manažerovi.</Oznameni>
                )}
                <div className="rounded-2xl border border-slate-200 px-4 py-1">
                  <RadekShrnuti popisek="Balíček">{nahled.shrnuti.balicek}</RadekShrnuti>
                  <RadekShrnuti popisek="Spolupráce">{nahled.shrnuti.spoluprace}</RadekShrnuti>
                  <RadekShrnuti popisek="První trénink">{nahled.shrnuti.prvni_trenink}</RadekShrnuti>
                  <RadekShrnuti popisek="První faktura">{nahled.shrnuti.prvni_faktura}</RadekShrnuti>
                  <RadekShrnuti popisek="Další faktury">{nahled.shrnuti.dalsi_faktury}</RadekShrnuti>
                  <RadekShrnuti popisek="Fakturovat na">{nahled.shrnuti.fakturovat_na}</RadekShrnuti>
                  {poznamka && <RadekShrnuti popisek="Poznámka k uzavření">{poznamka}</RadekShrnuti>}
                </div>
                {nahled.smlouva && (
                  <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 p-3">
                    <span className="text-[14px] text-slate-500">Smlouva</span>
                    <Pil ton="znacka" casti={[nahled.smlouva.nazev, `balíček ${nahled.smlouva.balicek}`]} />
                    {nahled.smlouva.verze ? <Pil ton="neutralni" casti={["verze", String(nahled.smlouva.verze)]} /> : null}
                    {rezim === "podepsana" && <Pil ton="ok" ikona={CircleCheck} casti="podepsaná" />}
                  </div>
                )}
                {rezim === "tablet" && <Oznameni ton="znacka" ikona={CircleCheck}><strong>Po podpisu se spolupráce uzavře sama.</strong> Vystaví se faktura, první trénink se zapíše do kalendáře a {d.muz ? "klientovi" : "klientce"} odejde uvítací e‑mail.</Oznameni>}
                {rezim === "na_dalku" && <Oznameni ton="znacka" ikona={CircleCheck}><strong>Faktura, první trénink a uvítací e‑mail se vystaví, až {d.muz ? "klient podepíše" : "klientka podepíše"}.</strong> Odkaz platí 24 hodin, první trénink bude do té doby zablokovaný.</Oznameni>}
                {rezim === "tablet" && <p className="text-[13px] text-slate-500">{d.muz ? "Klient" : "Klientka"} podepsat nechce? Zavři průvodce a zapiš <strong>Nerozhodnuto</strong>.</p>}
              </>
            )
          )}
          {krok === 4 && (podepsano
            ? <UzavreniPoPodpisu zkusebniId={polozka.id} d={d} onHotovo={(v) => { setHotovo(v); onHotovo?.(v); }}
                onRucne={() => { setD((x) => ({ ...x, pripraveno: null, podepsano: { balicek } })); setPodepsano(false); setPriprava(null); setNahled(null); setKrok(3); }} />
            : naDalku
              ? <PodpisNaDalku zkusebniId={polozka.id} d={d} smlouva={nahled?.smlouva} vybrany={vybrany} prvni={prvni} priprava={priprava}
                  onZnovu={() => pripravit(true)} onPodepsano={() => setPodepsano(true)} />
              : <PodpisNaMiste zkusebniId={polozka.id} d={d} u={u} smlouva={nahled?.smlouva} vybrany={vybrany} onPodepsano={() => setPodepsano(true)} />
          )}
        </div>
      )}
    </Modal>
  );
}

// ─── Karta „Po zkušebním: rozhodni“ ──────────────────────────────────────────
// Láďa 18. 10. 2026: údaje v pilulkách, ať jsou čitelné. Nerozhodnuto jde jen jednou navíc (celkem dvakrát).
const MAX_NEROZHODNUTO = 2;
const lzeOdlozit = (p) => (Number(p?.nerozhodnuto_pocet) || 0) < MAX_NEROZHODNUTO;
function zkusebniCasti(p) {
  const dnes = dnesIso();
  const den = p.datum === dnes ? "dnes" : p.datum === plusDny(dnes, -1) ? "včera" : dm(p.datum);
  return ["zkušební", den, cas(p.cas)];
}
function PilulkyZkusebni({ p }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Pil ton="neutralni" ikona={CalendarDays} casti={zkusebniCasti(p)} mala />
      <Pil ton="neutralni" ikona={MapPin} casti="VT GYM" mala />
      {p.vysledek === "nerozhodnuto" && !p.ceka_podpis_do && <Pil ton={p.dnes_volat ? "problem" : "pozor"} ikona={p.dnes_volat ? Phone : Hourglass} casti={p.dnes_volat ? ["dnes zavolat", p.telefon] : ["rozhodne se", dm(p.nerozhodnuto_datum)]} mala />}
      {/* 4. 11. 2026: smlouva odeslaná k podpisu na dálku, čeká se na podpis (24 hodin). */}
      {p.ceka_podpis_do && <Pil ton="znacka" ikona={Send} casti={["čeká na podpis do", `${p.ceka_podpis_do.slice(0, 10) === dnesIso() ? "dnes" : p.ceka_podpis_do.slice(0, 10) === plusDny(dnesIso(), 1) ? "zítra" : dm(p.ceka_podpis_do.slice(0, 10))} ${cas(p.ceka_podpis_do.slice(11, 16))}`]} mala />}
    </div>
  );
}
// 22. 10. 2026: čtvrtá volba Nedorazila. Na telefonu tři vedlejší tlačítka vedle sebe (ikona nad
// textem jako v sales), Uzavřít přes celou šířku.
const VEDLEJSI = "btn-secondary justify-center !min-h-[64px] !flex-col !gap-1 !px-1 !text-[13px] lg:!min-h-[44px] lg:!flex-row lg:!gap-2 lg:!px-4 lg:!text-[14px]";
const SLOUPCE = { 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3" };
function TlacitkaRozhodnuti({ p, onOtevri }) {
  const odlozit = lzeOdlozit(p);
  // U Nerozhodnuto klientka na zkušebním byla, Nedorazila tam nedává smysl.
  const nedorazila = p.vysledek !== "nerozhodnuto";
  const vedlejsich = 1 + (odlozit ? 1 : 0) + (nedorazila ? 1 : 0);
  return (
    <div className="space-y-2">
      {!odlozit && <Oznameni ton="pozor"><strong>Klientka teď musí říct výsledek.</strong> Rozhodnutí už se jednou odkládalo, další Nerozhodnuto nejde.</Oznameni>}
      <div className={`grid ${SLOUPCE[vedlejsich]} gap-2 lg:flex lg:flex-nowrap lg:justify-end`}>
        {nedorazila && <button type="button" className={VEDLEJSI} onClick={() => onOtevri({ typ: "nedorazila", polozka: p })}><UserMinus className="h-5 w-5 lg:h-4 lg:w-4" />Nedorazila</button>}
        <button type="button" className={VEDLEJSI} onClick={() => onOtevri({ typ: "nezacne", polozka: p })}><UserX className="h-5 w-5 lg:h-4 lg:w-4" />Nezačne</button>
        {odlozit && <button type="button" className={VEDLEJSI} onClick={() => onOtevri({ typ: "nerozhodnuto", polozka: p })}><Hourglass className="h-5 w-5 lg:h-4 lg:w-4" />Nerozhodnuto</button>}
        <button type="button" className="btn-primary col-span-full justify-center" onClick={() => onOtevri({ typ: "uzavrit", polozka: p })}><Handshake className="h-4 w-4" />Uzavřít spolupráci</button>
      </div>
    </div>
  );
}
export default function KartaPoZkusebnim({ trenerId = null, trener = null, className = "", onPocet = null }) {
  const [polozky, setPolozky] = useState(null);
  const [otevreno, setOtevreno] = useState(null); // { typ, polozka }
  // trener = jméno (owner v náhledu trenéra na přehledu), server ho bere jen od vedení.
  const nacti = () => dealGet("po_zkusebnim", trenerId ? { trener_id: trenerId } : trener ? { trener } : {}).then((r) => { setPolozky(r.polozky || []); onPocet?.((r.polozky || []).length); }).catch(() => { setPolozky([]); onPocet?.(0); });
  useEffect(() => { nacti(); }, [trenerId, trener]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!polozky || (!polozky.length && !otevreno)) return null;
  // Okno se zavře až s novým seznamem, ať vyřízený zkušební hned zmizí (23. 10. 2026).
  const hotovo = async () => { await nacti(); setOtevreno(null); };
  const zavri = () => { setOtevreno(null); nacti(); };
  return (
    <>
      <KartaObal className={className}>
        <div className="flex items-center gap-2.5 border-b border-slate-100 px-4 py-3">
          <Handshake className="h-5 w-5 text-brand" />
          <h3 className="min-w-0 flex-1 text-[16px] font-bold text-slate-900">Po zkušebním: rozhodni</h3>
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[12px] font-bold tabular-nums text-amber-800">{polozky.length}</span>
        </div>
        {polozky.map((p) => (
          <div key={p.id} className="flex flex-col gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0 lg:flex-row lg:items-center">
            <div className="min-w-0 flex-1 space-y-1.5">
              <div className="text-[16px] font-bold text-brand-deep">{p.jmeno}</div>
              <PilulkyZkusebni p={p} />
            </div>
            {/* Mobil: dvě vedlejší tlačítka vedle sebe, Uzavřít přes celou šířku (schváleno 17. 10.). */}
            <div className="lg:min-w-[440px]"><TlacitkaRozhodnuti p={p} onOtevri={setOtevreno} /></div>
          </div>
        ))}
      </KartaObal>
      <DealPruvodce key={otevreno?.polozka?.id || "zavreno"} open={otevreno?.typ === "uzavrit"} polozka={otevreno?.polozka} onClose={zavri} />
      <NezacneDialog open={otevreno?.typ === "nezacne"} polozka={otevreno?.polozka} onClose={zavri} onHotovo={hotovo} />
      <NerozhodnutoDialog open={otevreno?.typ === "nerozhodnuto"} polozka={otevreno?.polozka} onClose={zavri} onHotovo={hotovo} />
      <NedorazilaDialog open={otevreno?.typ === "nedorazila"} polozka={otevreno?.polozka} onClose={zavri} onHotovo={hotovo} />
    </>
  );
}

// ─── Okno zkušebního tréninku v kalendáři (Láďa 18. 10. 2026) ────────────────
// Po začátku zkušebního ukáže stejná tři tlačítka jako karta na přehledu. Dialog otevírá rodič
// (okno rezervace se místo sebe přepne na průvodce, dvě okna přes sebe nejsou).
export function RozhodnutiZkusebni({ bookingId, onOtevri }) {
  const [st, setSt] = useState(null);
  useEffect(() => {
    if (!bookingId) return undefined;
    let zive = true;
    dealGet("zkusebni", { booking_id: bookingId }).then((r) => { if (zive) setSt(r); }).catch(() => {});
    return () => { zive = false; };
  }, [bookingId]);
  if (!st?.polozka) return null;
  const p = st.polozka;
  if (st.vysledek === "uzavreno") return <Oznameni ton="ok" ikona={CircleCheck}><strong>Spolupráce uzavřena.</strong></Oznameni>;
  if (st.vysledek === "nezacne") return <Oznameni ton="info" ikona={UserX}><strong>Nezačne spolupráci.</strong></Oznameni>;
  if (st.vysledek === "nedorazila") return <Oznameni ton={st.omluva ? "pozor" : "problem"} ikona={UserMinus}><strong>Nedorazila {st.omluva ? "s omluvou" : "bez omluvy"}.</strong> {st.omluva ? "Sales jí zavolá kvůli novému termínu." : "Kontakt je v NQ lead."}</Oznameni>;
  // 22. 10. 2026: před koncem zkušebního (od 24 hodin před začátkem) jde zapsat jen Nedorazila.
  if (!st.muze_rozhodnout && st.muze_nedorazila) {
    return (
      <button type="button" className="btn w-full justify-center border border-rose-300 bg-white text-rose-700 !min-h-[48px]" onClick={() => onOtevri({ typ: "nedorazila", polozka: p })}>
        <UserMinus className="h-4 w-4" />Nedorazila na zkušební
      </button>
    );
  }
  if (!st.muze_rozhodnout) return null;
  return (
    <div className="space-y-2.5 rounded-2xl border border-brand/25 bg-brand-50/60 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Handshake className="h-5 w-5 text-brand" />
        <span className="text-[15px] font-bold text-slate-900">Po zkušebním: rozhodni</span>
        {p.vysledek === "nerozhodnuto" && <Pil ton={p.dnes_volat ? "problem" : "pozor"} ikona={p.dnes_volat ? Phone : Hourglass} casti={p.dnes_volat ? ["dnes zavolat", p.telefon] : ["rozhodne se", dm(p.nerozhodnuto_datum)]} mala />}
      </div>
      <TlacitkaRozhodnuti p={p} onOtevri={onOtevri} />
    </div>
  );
}

// Dialog vybraný v okně zkušebního. Po zápisu zavolá onHotovo (rodič obnoví kalendář a zavře okno),
// zavření bez zápisu vrátí okno rezervace (onZpet).
export function DialogZkusebni({ volba, onZpet, onHotovo }) {
  const [hotovo, setHotovo] = useState(false);
  if (!volba) return null;
  if (volba.typ === "uzavrit") return <DealPruvodce open polozka={volba.polozka} zpetText="Zavřít" onClose={() => (hotovo ? onHotovo() : onZpet())} onHotovo={() => setHotovo(true)} onZmena={() => setHotovo(true)} />;
  if (volba.typ === "nezacne") return <NezacneDialog open polozka={volba.polozka} onClose={onZpet} onHotovo={onHotovo} />;
  if (volba.typ === "nedorazila") return <NedorazilaDialog open polozka={volba.polozka} onClose={onZpet} onHotovo={onHotovo} />;
  return <NerozhodnutoDialog open polozka={volba.polozka} onClose={onZpet} onHotovo={onHotovo} />;
}

// ─── Nepodepsaná smlouva po uzavření po telefonu (Láďa 18. 10. 2026) ─────────
// Červený štítek s mírně pulzující tečkou, ať si ho trenér všimne. Stejný v kalendáři i v kartě klientky.
export function StitekPodepsat({ mala = false }) {
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-rose-600 font-bold text-white ${mala ? "px-1.5 py-px text-[10px] uppercase tracking-wider" : "px-2.5 py-1 text-[13px]"}`}>
      <span className="relative flex h-2 w-2 shrink-0"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-70" /><span className="relative inline-flex h-2 w-2 rounded-full bg-white" /></span>
      <FilePenLine className={mala ? "h-2.5 w-2.5 shrink-0" : "h-3.5 w-3.5 shrink-0"} />Podepsat smlouvu
    </span>
  );
}

// Záloha „Podepsáno na papíře“ platí do 31. 12. 2026 (pražský čas), pak zmizí.
const PAPIR_DO = "2026-12-31";
function papirPovolen() {
  const dnes = new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Prague" }).format(new Date());
  return dnes <= PAPIR_DO;
}

// Blok s tlačítkem Podepsat na tabletu (okno tréninku i karta klientky). 3. 11. 2026: podpis přes QR kód
// na tabletu a SMS v telefonu klientky, po podpisu se smlouva potvrdí sama. Ruční potvrzení zůstává
// pro smlouvu podepsanou jinak (papír).
export function BlokPodepsat({ dealId, kdy = null, onHotovo, className = "" }) {
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  const [tablet, setTablet] = useState(false);
  const potvrdit = async () => {
    setBusy(true); setChyba("");
    try { await dealPost("smlouva_podepsana", { deal_id: dealId }); onHotovo?.(); }
    catch (e) { setChyba(e.message); } finally { setBusy(false); }
  };
  return (
    <div className={`space-y-2.5 rounded-2xl border border-rose-200 bg-rose-50 p-3 ${className}`}>
      <StitekPodepsat />
      <div className="text-[14px] leading-snug text-rose-950">Smlouvu klientka podepíše na prvním tréninku{kdy ? <> <strong>{kdy}</strong></> : null}. Klepni na <strong>Podepsat na tabletu</strong> a QR kód načti tabletem.</div>
      {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
      <button type="button" className="btn w-full min-h-[52px] justify-center bg-emerald-600 text-base text-white hover:bg-emerald-700" onClick={() => setTablet(true)}><FilePenLine className="h-4 w-4" />Podepsat na tabletu</button>
      {/* Láďa 3. 11. 2026: ruční potvrzení papírové smlouvy jen jako záloha do konce roku 2026. */}
      {papirPovolen() && <button type="button" className="btn-secondary w-full justify-center" onClick={potvrdit} disabled={busy}><CircleCheck className="h-4 w-4" />{busy ? "Ukládám…" : "Podepsáno na papíře"}</button>}
      {tablet && (
        <Modal open onClose={() => setTablet(false)} size="md" plnaHlavicka header={<HlavickaOkna ikona={FilePenLine} nazev="Podepsat smlouvu" popis="QR kód načti tabletem" onClose={() => setTablet(false)} />}
          footer={<button type="button" className="btn-secondary" onClick={() => setTablet(false)}>Zavřít</button>}>
          <div className="space-y-4">
            <CoRict>Teď spolu podepíšeme smlouvu. Na tabletu si ji v klidu přečtěte, podpis pak potvrdíte ve svém telefonu přes SMS.</CoRict>
            <PodpisQr dealId={dealId} onPodepsano={() => { setTimeout(() => { setTablet(false); onHotovo?.(); }, 2500); }} />
          </div>
        </Modal>
      )}
    </div>
  );
}

// Karta klientky: blok se ukáže jen trenérovi té klientky (a vedení), když smlouva čeká na podpis.
export function PodpisSmlouvyKlientky({ klientId, className = "" }) {
  const [st, setSt] = useState(null);
  useEffect(() => {
    if (!klientId) return undefined;
    let zive = true;
    dealGet("smlouva_klientky", { klient_id: klientId }).then((r) => { if (zive) setSt(r.smlouva || null); }).catch(() => {});
    return () => { zive = false; };
  }, [klientId]);
  if (!st) return null;
  const kdy = st.datum ? `${DNY_KRATCE[new Date(`${st.datum}T12:00:00Z`).getUTCDay()]} ${dm(st.datum)}${st.cas ? ` v ${cas(st.cas)}` : ""}` : null;
  return <BlokPodepsat dealId={st.deal_id} kdy={kdy} className={className} onHotovo={() => setSt(null)} />;
}
